/**
 * IslandRenderer — element renderer for `type: "island"` nodes.
 *
 * Mounts a host-registered React component under an `<IslandRegistry>` name
 * with `resolveSymbol`-evaluated props. Provides the escape hatch for cogo
 * UI trees that need to embed bespoke React state/hooks/providers without
 * reconstructing them in JSON.
 *
 * See .planning/feature-island-design.md for the full design.
 */
import { Suspense } from "react"
import type { RendererProps } from "../core/element-registry"
import { useRenderContext } from "../core/render-context"

/** Detect expressions the renderer should hand to `resolveSymbol`.
 * Matches v2 renderer convention — strings starting with `#{` or `@{`. */
function isSymbolExpression(value: unknown): value is string {
  if (typeof value !== "string") return false
  return value.startsWith("#{") || value.startsWith("@{")
}

/** Resolve top-level string expressions in a props object. Nested objects /
 * arrays are passed through without recursion — per §4 of the design doc,
 * consumers who need nested expressions should flatten them into top-level
 * keys instead. */
function resolveProps(
  raw: Record<string, unknown>,
  resolve: (expr: string) => unknown,
): Record<string, unknown> {
  const out: Record<string, unknown> = {}
  for (const [key, value] of Object.entries(raw)) {
    out[key] = isSymbolExpression(value) ? resolve(value) : value
  }
  return out
}

export function IslandRenderer({ element }: RendererProps) {
  const ctx = useRenderContext()
  const name = typeof element.island === "string" ? element.island : ""
  const rawProps = element.props && typeof element.props === "object"
    ? element.props
    : Object.fromEntries(
        Object.entries(element).filter(
          ([k]) => !["id", "type", "island", "fallback", "children", "style", "className", "animation", "text", "props"].includes(k)
        )
      )
  const fallbackText = typeof element.fallback === "string" ? element.fallback : "Loading…"

  if (!name) {
    console.warn("[cogo-renderer] island node is missing 'island' name", element.id)
    return null
  }

  if (!ctx.islands) {
    console.warn(
      `[cogo-renderer] no IslandRegistry in RenderContext — cannot mount island "${name}" (id=${element.id})`,
    )
    return null
  }

  const Island = ctx.islands.get(name)
  if (!Island) {
    console.warn(
      `[cogo-renderer] unknown island "${name}" (id=${element.id}). ` +
        `Known: [${ctx.islands.list().join(", ")}]`,
    )
    return null
  }

  const resolvedProps = resolveProps(rawProps, ctx.resolveSymbol)

  // Deliver the host flow callback to islands that opt into it (e.g. a game
  // island's `onFlow(flowBinding, eventType)` prop). JSON props can't carry a
  // function, so this is the sole delivery path; we only inject when the host
  // provided `ctx.onFlow` AND the node did not already set its own `onFlow`,
  // and never for non-island consumers (this renderer only runs for
  // `type: "island"` nodes). Absent `ctx.onFlow` → the prop stays undefined,
  // exactly as before.
  //
  // Contract notes:
  //  - The injection is type-agnostic: under a host that sets `ctx.onFlow`
  //    (AppV2 always does), EVERY mounted island receives an `onFlow` function
  //    prop. Islands registered for that path must NOT blind-spread `{...props}`
  //    onto a DOM node (React would warn on an unknown `onFlow` attribute) — read
  //    the props you declare, like PixiStageIsland's `({ flow, onFlow }) => …`.
  //  - A node may set `props.onFlow` to ANY non-undefined value (incl. `null`) to
  //    suppress host injection — the guard is `=== undefined`, not a falsy check.
  if (ctx.onFlow && resolvedProps.onFlow === undefined) {
    resolvedProps.onFlow = ctx.onFlow
  }

  const island = (
    <Suspense
      fallback={
        <div
          data-cogo-island-fallback={name}
          data-fallback={fallbackText}
          className="cogo-island-fallback flex items-center justify-center text-sm text-muted-foreground"
        >
          {fallbackText}
        </div>
      }
    >
      <Island {...resolvedProps} />
    </Suspense>
  )

  return (
    <div
      data-cogo-island-host={name}
      data-fallback={fallbackText}
      aria-description={fallbackText}
      className={element.className ?? "contents"}
    >
      {island}
    </div>
  )
}
